// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

// ========================================
// Nudge Provider Types
// ========================================

/**
 * Email template for nudge notifications.
 *
 * <p>The {@code notificationSubject}/{@code notificationBodyHtml} pair was
 * retired when provider nudges unified on the customBody-injection pattern;
 * now there's a single default and the advisor's edit gets injected into
 * the shared notification template at send time.
 */
export interface NudgeEmailTemplate {
  subject: string;
  bodyHtml: string;
  templateId?: string;
  templateLevel?: string;
}

/**
 * LOA information from the API
 */
export interface LoaInfoDTO {
  loaId: string;
  loaReference: string;
  status: string;
  completedAt: string;
  originalNotificationSentAt?: string;
}

/**
 * Policy information from the API
 */
export interface PolicyInfoDTO {
  policyId: string;
  policyNumber: string | null;
  providerId: string;
  providerName: string;
  providerEmail: string;
  policyTypeName: string | null;
  profileId: string;
  profileName: string;
  hasLoaSlice: boolean;
  loaSlicePath?: string;
}

/**
 * Individual nudge history entry from the API
 */
export interface NudgeHistoryEntryDTO {
  notificationId: string;
  sentAt: string;
  sentBy: string;
  recipientEmail: string;
  subject: string;
  status: string;
  hasAttachment: boolean;
  errorMessage?: string;
  retryCount: number;
}

/**
 * Nudge history from the API
 */
export interface NudgeHistoryDTO {
  policyId: string;
  totalNudgeCount: number;
  successfulNudgeCount: number;
  failedNudgeCount: number;
  firstNudgeAt?: string;
  lastNudgeAt?: string;
  entries: NudgeHistoryEntryDTO[];
}

/**
 * Rate limit thresholds from the API
 */
export interface RateLimitThresholds {
  maxPerPolicyPerDay: number;
  maxPerProviderPerDay: number;
  maxPerProviderPerWeek: number;
  maxPerProviderPerMonth: number;
  minHoursBetweenNudges: number;
}

/**
 * Rate limit status from the API
 */
export interface RateLimitStatusDTO {
  warningCode: 'NONE' | 'RECENTLY_SENT' | 'DAILY_LIMIT_APPROACHING' | 'PROVIDER_LIMIT_APPROACHING' | 'BLOCKED';
  warningMessage?: string;
  nudgesForPolicyToday: number;
  nudgesForProviderToday: number;
  nudgesForProviderThisWeek: number;
  nudgesForProviderThisMonth: number;
  totalNudgesForPolicy: number;
  lastNudgeAt?: string;
  thresholds: RateLimitThresholds;
}

/**
 * Complete response from nudge data endpoint
 */
export interface NudgeDataResponse {
  policyInfo: PolicyInfoDTO;
  loaInfo: LoaInfoDTO;
  defaultTemplate: NudgeEmailTemplate;
  rateLimitStatus: RateLimitStatusDTO;
  nudgeHistory: NudgeHistoryDTO;
}

/**
 * Request to send a nudge email
 */
export interface NudgeRequest {
  policyId: string;
  loaId: string;
  recipientEmail: string;
  subject: string;
  bodyHtml: string;
  includeAttachment?: boolean;
  acknowledgeWarning?: boolean;
  // useNotificationTemplate was removed when provider nudges unified on
  // the customBody-injection pattern — the server always injects the
  // advisor's paragraph into the notification template now.
}

/**
 * Result of a nudge operation
 */
export interface NudgeResult {
  success: boolean;
  message?: string;
  recipientEmail?: string;
  notificationId?: string;
  status?: string;
  warningCode?: string;
  errorMessage?: string;
}

/**
 * Modal state for nudge operations
 */
export type NudgeModalState = 'loading' | 'ready' | 'editing' | 'sending' | 'success' | 'error';

/**
 * Button variant for nudge button
 */
export type NudgeButtonVariant = 'button' | 'icon' | 'text';

/**
 * Props for NudgeProviderButton component
 */
export interface NudgeProviderButtonProps {
  policyId: string;
  /**
   * Relationship/profile ID needed for the navigation route
   * ({@code /relationships/:relationshipId/policies/:policyId/nudge}).
   * Replaces the previous modal trigger.
   */
  relationshipId: string;
  hasCompletedLoa: boolean;
  variant?: NudgeButtonVariant;
  size?: 'mini' | 'tiny' | 'small' | 'medium' | 'large';
  disabled?: boolean;
  label?: string;
}

// NudgeProviderModalProps + NudgeEmailEditorProps removed along with their
// components — the compose surface is now NudgeProviderPage.

/**
 * Props for NudgeHistoryDisplay component
 */
export interface NudgeHistoryDisplayProps {
  history: NudgeHistoryDTO;
  loading?: boolean;
}

/**
 * Props for PolicyNudgeInfo component
 */
export interface PolicyNudgeInfoProps {
  policyInfo: PolicyInfoDTO;
  loaInfo: LoaInfoDTO;
}

/**
 * Props for NudgeRateLimitWarning component
 */
export interface NudgeRateLimitWarningProps {
  rateLimitStatus: RateLimitStatusDTO;
}

// Helper function to check if nudging is allowed based on warning code
export function canNudge(rateLimitStatus: RateLimitStatusDTO): boolean {
  return rateLimitStatus.warningCode !== 'BLOCKED';
}
